// components/climate/flood/FloodDepth.tsx
import { useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, TextField, MenuItem, Stack } from '@mui/material'
import { type ChartOptions, type ChartData } from 'chart.js'
import { Line } from 'react-chartjs-2'
import { EmissionTier, MetricState } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import type { MetricDataFilters } from '../../../../hooks/analytics'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { formatFloodSourceLabel } from '../../common/financialMetrics'

type FloodPoint = { x: number; y: number }

export default function FloodDepth({
  propertyId,
  emissionTier,
  filters,
  showBarrierHeight = false,
  barrierHeightMeters = 0.8,
}: {
  propertyId: number
  emissionTier: EmissionTier
  filters?: MetricDataFilters | null
  showBarrierHeight?: boolean
  barrierHeightMeters?: number
}) {
  const floodQueryParams = useMemo(() => (filters ? { filters } : undefined), [filters])
  const {
    data: floodDepth,
    isLoading: floodDepthInitialLoading,
    isFetching: floodDepthFetching,
    error,
  } = usePropertyClimateData('fld:depth', { propertyId }, floodQueryParams)
  const {
    data: floodArea,
    isLoading: floodAreaInitialLoading,
    isFetching: floodAreaFetching,
  } = usePropertyClimateData('fld:affected_area', { propertyId }, floodQueryParams)
  const isLoading = floodDepthInitialLoading || floodDepthFetching || floodAreaInitialLoading || floodAreaFetching

  const [selectedSource, setSelectedSource] = useState<string | null>(null)
  const [selectedReturnPeriod, setSelectedReturnPeriod] = useState<string | null>(null)

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Could not load flood depth data.</Alert>
  }

  if (!floodDepth) {
    return <Alert severity="info">No flood depth data available for this property.</Alert>
  }

  if (floodDepth.state.value !== MetricState.SUCCEEDED) {
    return (
      <Alert severity="warning">Flood depth calculations are not available (state: {floodDepth.state.value}).</Alert>
    )
  }

  const tierDepthData = floodDepth.data.data[emissionTier]
  if (!tierDepthData) {
    return (
      <Alert severity="info">
        No flood depth data for emission tier <strong>{emissionTier}</strong>.
      </Alert>
    )
  }

  const sources: string[] = floodDepth.data.keys.source || []
  const returnPeriods: string[] = floodDepth.data.keys.return_period || []

  if (!sources.length || !returnPeriods.length) {
    return <Alert severity="info">Flood depth data is empty for this property.</Alert>
  }

  const effectiveSource = selectedSource && sources.includes(selectedSource) ? selectedSource : sources[0]

  const defaultReturnPeriod = returnPeriods.includes('100') ? '100' : returnPeriods[0]
  const effectiveReturnPeriod =
    selectedReturnPeriod && returnPeriods.includes(selectedReturnPeriod) ? selectedReturnPeriod : defaultReturnPeriod

  const hasAreaMetric =
    floodArea && floodArea.state?.value === MetricState.SUCCEEDED && floodArea.data?.data?.[emissionTier]

  const areaTierData = hasAreaMetric ? floodArea!.data.data[emissionTier] : undefined

  // Build combined chart data for the selected source + return period
  const buildCombinedChartData = (): ChartData<'line'> => {
    const depthSeries = ((tierDepthData?.[effectiveSource]?.[effectiveReturnPeriod] ?? []) as FloodPoint[]) || []
    const areaSeries = ((areaTierData?.[effectiveSource]?.[effectiveReturnPeriod] ?? []) as FloodPoint[]) || []
    const baseSeries = depthSeries.length ? depthSeries : areaSeries
    const labels = baseSeries.map(p => String(p.x))
    const datasets: ChartData<'line'>['datasets'] = []

    if (depthSeries.length) {
      datasets.push({
        label: 'Water depth (m)',
        data: depthSeries.map(p => p.y),
        yAxisID: 'depth',
        borderColor: '#1976d2',
        backgroundColor: '#1976d2',
        tension: 0.2,
        pointRadius: 3,
      })
    }

    if (showBarrierHeight) {
      datasets.push({
        label: 'Flood barrier height (m)',
        data: labels.map(() => barrierHeightMeters),
        yAxisID: 'depth',
        borderColor: '#2e7d32',
        backgroundColor: '#2e7d32',
        tension: 0,
        pointRadius: 0,
        borderDash: [6, 4],
      })
    }

    if (hasAreaMetric && areaSeries.length) {
      datasets.push({
        label: 'Property area flooded (%)',
        data: areaSeries.map(p => p.y * 100),
        yAxisID: 'area',
        borderColor: '#ff9800',
        backgroundColor: '#ff9800',
        tension: 0.2,
        pointRadius: 3,
        borderDash: [5, 4],
      })
    }

    return { labels, datasets }
  }

  const chartData = buildCombinedChartData()

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: ctx => {
            const label = ctx.dataset.label || ''
            const value = ctx.parsed.y as number
            if (ctx.dataset.yAxisID === 'area') {
              return `${label}: ${value.toFixed(1)}%`
            }
            return `${label}: ${value.toFixed(2)} m`
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
        },
      },
      depth: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Water depth (m)',
        },
        beginAtZero: true,
        suggestedMax: 1.0,
      },
      area: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Property area flooded (%)',
        },
        beginAtZero: true,
        suggestedMax: 100,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  }

  const hasData = chartData.labels && chartData.labels.length && chartData.datasets.length

  return (
    <Box>
      <Stack
        justifyContent="space-between"
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        gap={2}
      >
        <ClimateSectionTitle
          title="Flood Depth & Coverage"
          subtitle="Projected flood depth and property area affected across available years"
          helpText="This chart shows the full projection series for the selected emission tier, flood source, and return period. It is not filtered by the header horizon."
        />

        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <TextField
            select
            size="small"
            label="Source"
            value={effectiveSource}
            onChange={e => setSelectedSource(e.target.value)}
          >
            {sources.map(source => (
              <MenuItem key={source} value={source}>
                {formatFloodSourceLabel(source)} flood
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Return period"
            value={effectiveReturnPeriod}
            onChange={e => setSelectedReturnPeriod(e.target.value)}
          >
            {returnPeriods.map(rp => (
              <MenuItem key={rp} value={rp}>
                {rp}-year event
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Stack>

      {hasData ? (
        <Box sx={{ height: 260 }}>
          <Line data={chartData} options={options} />
        </Box>
      ) : (
        <Alert severity="info" sx={{ my: 14.2 }}>
          No data available for the selected source and return period.
        </Alert>
      )}
    </Box>
  )
}
